import { useQuery } from 'react-query';
import { designHuddleService } from 'services';
import { ServerStateKeysEnum } from '../constants';

/**
 * Custom React Query hook to fetch a Design Huddle project.
 *
 * @param {string | undefined} clinid - The client ID associated with the project.
 * @param {string} projectId - The unique identifier of the project to fetch.
 * @param {boolean} [isDisabled] - Optional flag to disable the query.
 *
 * This hook uses the `react-query` library to fetch project data from the `designHuddleService`.
 * - The query key is constructed using the `ServerStateKeysEnum.designHuddleProjects` and `projectId`.
 * - The query is enabled only if `clinid` and `projectId` are provided and `isDisabled` is not true.
 * - The query does not refetch on window focus and has an infinite stale time.
 *
 * Use Cases with Download Agreement modal:
 * - Modal opened from Design Huddle Editor to fetch project details.
 * - Modal opened from Marketing Drafts to fetch project details.
 * - Modal opened from Product Details Page (no project id), should NOT fetch without `projectId`.
 */
export const useDesignHuddleProject = (
  clinid: string | undefined,
  projectId: string,
  isDisabled?: boolean
) => {
  return useQuery(
    [`${ServerStateKeysEnum.designHuddleProjects}`, projectId],
    () => {
      return designHuddleService.getProject(clinid, projectId);
    },

    {
      staleTime: Infinity,
      enabled: !!clinid && !isDisabled && !!projectId,
      refetchOnWindowFocus: false,
    }
  );
};
